// UI language and direction (UI-P3, critique decision 5). The root's lang and dir follow the READING language, and the
// chrome is drawn in it. MAJLIS-164 (rulings 1-5): every "I read in" language is an interface language (UiLang, the
// catalogue's tag); its words come from catalogues/ (en in the main bundle, every other language a lazy chunk), its
// plural forms from the platform's CLDR rules, its dates, numbers and language names from Intl in the full tag.
import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { UI_LANGS, catalogueLangOf, catalogues, type Catalogue, type CatalogueStore, type UiLang } from "./catalogues";
import { en, type StringKey } from "./catalogues/en";

export { UI_LANGS, catalogueLangOf, catalogues, type UiLang } from "./catalogues";
export type { StringKey } from "./catalogues/en";
export type Translate = (key: StringKey, vars?: Record<string, string | number>) => string;

/** The right-to-left languages: the ONE place the set lives (dirOf, isRtl, applyRoot all read it). */
const RTL_PRIMARY = new Set(["ar", "fa"]);

export function primary(tag: string): string {
  return tag.split("-")[0].toLowerCase();
}

export function isRtl(tag: string): boolean {
  return RTL_PRIMARY.has(primary(tag));
}

export function dirOf(tag: string): "rtl" | "ltr" {
  return isRtl(tag) ? "rtl" : "ltr";
}

/** Chinese read in Traditional script: a Hant tag, or a region that writes it (Taiwan, Hong Kong, Macau). The script
 * subtag decides first (PR 1 review, 2026-10-04): a tag that names Hans reads Simplified whatever its region
 * (zh-Hans-HK, zh-Hans-TW); only a tag with no Hans falls to the region list. */
export function readsTraditional(tag: string): boolean {
  if (/-hans\b/i.test(tag)) return false;
  return /-(hant|tw|hk|mo)\b/i.test(tag);
}

const UI_BY_LOWER = new Map<string, UiLang>(UI_LANGS.map((l) => [l.toLowerCase(), l]));

/** MAJLIS-164 (ruling 2): the interface language for a reading language - the tag itself for every READ_LANGUAGES tag;
 * zh-Hans and zh-Hant stay distinct (bare or regional Chinese picks its script); Cantonese reads Traditional Chinese; a
 * regional tag falls to its language (ar-EG -> ar); anything else is English. */
export function uiLangFor(readLang: string): UiLang {
  const tag = readLang.trim();
  const p = primary(tag);
  if (p === "yue") return "zh-Hant";
  if (p === "zh") return readsTraditional(tag) ? "zh-Hant" : "zh-Hans";
  return UI_BY_LOWER.get(tag.toLowerCase()) ?? UI_BY_LOWER.get(p) ?? "en";
}

/** A translate function over one catalogue: the key's value with each {name} filled in. */
export function translatorOf(table: Catalogue): Translate {
  return (key, vars) => {
    let s = table[key];
    if (vars) for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(String(v));
    return s;
  };
}

/** The translator for `lang` from the app's store: its catalogue when in hand, else English (never a bare key). */
export function translator(lang: UiLang, store: CatalogueStore = catalogues): Translate {
  return translatorOf(store.ready(lang) ?? en);
}

/** MAJLIS-164 (ruling 3): the CLDR plural category of `n` in `lang`, as the platform's Intl.PluralRules computes it.
 * The catalogues carry the five forms one/two/few/many/other (en is the key source); CLDR's "zero" (Arabic 0) has no
 * form of its own and uses "other", as Arabic writes it. pluralIntl.test.ts holds this against the hand-written en and
 * ar rules the app used before (n = 0..200). */
export type PluralCategory = "one" | "two" | "few" | "many" | "other";
const pluralRules = new Map<string, Intl.PluralRules>();
export function pluralCategory(lang: UiLang, n: number): PluralCategory {
  let rules = pluralRules.get(lang);
  if (!rules) {
    rules = new Intl.PluralRules(lang);
    pluralRules.set(lang, rules);
  }
  const c = rules.select(n);
  return c === "zero" ? "other" : c;
}

/** The keys of a counted string: `<base>_one`, `<base>_two`, ... (every language defines all five). */
export type PluralBase = { [K in StringKey]: K extends `${infer B}_other` ? B : never }[StringKey];

/** A counted string: the form for `n`'s category, with {n} (and any other vars) filled in. The category follows the
 * language whose words `t` draws (catalogueLangOf: English until a language has its own catalogue). */
export function plural(lang: UiLang, t: Translate, base: PluralBase, n: number, vars: Record<string, string | number> = {}) {
  return t(`${base}_${pluralCategory(catalogueLangOf(lang), n)}` as StringKey, { ...vars, n });
}

/** MAJLIS-164: the catalogue a reading language's interface is drawn in, fetched (at once when it is in hand). The ONE
 * loader the app calls before its first paint (main.tsx), and the one the test suite calls before it starts
 * (test/catalogues.setup.ts). */
export function loadInterface(readLang: string, store: CatalogueStore = catalogues): Promise<Catalogue> {
  return store.load(store.wordsOf(uiLangFor(readLang)));
}

/** Apply the reading language to the document root (lang + dir). */
export function applyRoot(doc: Document, readLang: string): void {
  doc.documentElement.lang = readLang;
  doc.documentElement.dir = dirOf(readLang);
}

export interface I18n {
  lang: UiLang;
  t: Translate;
}
/** useI18nFor's answer: also whether the reading language's own catalogue is the one drawn (false while it loads). */
export interface DrawnI18n extends I18n {
  ready: boolean;
}
export const I18nContext = createContext<I18n>({ lang: "en", t: translator("en") });
export const useI18n = () => useContext(I18nContext);

interface Shown {
  lang: UiLang;
  table: Catalogue;
}

/** MAJLIS-164 (ruling 1): the interface for a reading language. A catalogue in hand is drawn at once; a lazy one is
 * fetched, and until it arrives the catalogue drawn last stays (English at first) - the screen never shows a bare key.
 * A failed fetch keeps what is shown and is reported through `onError`; the next change of language tries again.
 * `lang` is ALWAYS the language of the words drawn (store.wordsOf): its dates, numbers, language names, plural forms
 * and the chrome's lang/dir all agree with the words. So a reading language still drawn in the English catalogue keeps
 * today's English interface whole (no French dates inside English sentences, no rtl English for fa) until its own
 * catalogue lands, and then switches entirely. */
export function useI18nFor(
  readLang: string,
  store: CatalogueStore = catalogues,
  onError?: (lang: UiLang, err: unknown) => void,
): DrawnI18n {
  const target = store.wordsOf(uiLangFor(readLang));
  const now = store.ready(target);
  const [shown, setShown] = useState<Shown>(() => (now ? { lang: target, table: now } : { lang: "en", table: en }));
  // React's "adjust state while rendering": the catalogue in hand becomes the one drawn last
  if (now && (shown.lang !== target || shown.table !== now)) setShown({ lang: target, table: now });
  useEffect(() => {
    if (store.ready(target)) return;
    let live = true;
    store.load(target).then(
      (table) => {
        if (live) setShown({ lang: target, table });
      },
      (err: unknown) => onError?.(target, err),
    );
    return () => {
      live = false;
    };
  }, [store, target, onError]);
  const lang = now ? target : shown.lang;
  const table = now ?? shown.table;
  const ready = now !== undefined;
  return useMemo(() => ({ lang, t: translatorOf(table), ready }), [lang, table, ready]);
}

/** MAJLIS-164 ("whole or hold", the owner's rule): the root's lang and dir follow the reading language (UI-P3) only once
 * its interface is drawn; while its catalogue loads the root keeps what it had, so no frame shows the previous
 * catalogue's words under the new language's direction. A layout effect: applied before the browser paints the
 * commit that drew the words. */
export function useApplyRoot(doc: Document, readLang: string, ready: boolean): void {
  useLayoutEffect(() => {
    if (ready) applyRoot(doc, readLang);
  }, [doc, readLang, ready]);
}
